import {
  CohortBuilderDefinitionLegacy,
  CohortBuilderItemChangeStatusLegacy,
  CohortDslLegacy,
  DerivedEventLegacy,
  IndexDateTypeLegacy,
  IndexDateVariableLegacy,
} from '@verantos-dev/types-cohorts';
import {
  getFullDslQueryString,
  transformDerivedEventsToDsl,
  transformIndexDateToDsl,
  transformSubjectVariablesToDsl,
} from '@verantos-dev/util-cohort-definition-ui-to-dsl';

import { computeDiffForCohortBuilderItem } from '../computeDiffForCohortBuilderItem';
import { getNextDerivedEventsForDerivedEventNameChange } from '../getNextDerivedEventsForDerivedEventNameChange';
import { getNextSubjectVariablesForDerivedEventNameChange } from '../getNextSubjectVariablesForDerivedEventNameChange';

export const getFullCohortDslForDerivedEvent = (
  currentCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  nextDerivedEvent: DerivedEventLegacy
) => {
  const currentSubjectVariables = currentCohortDefinition?.subjectVariables ?? [];
  const currentIndexDate = currentCohortDefinition?.indexDate;
  const currentDerivedEvents = currentCohortDefinition?.derivedEvents ?? [];
  const payloadDerivedEventIndex = currentDerivedEvents.findIndex(
    (derivedEvents) => derivedEvents.id === nextDerivedEvent.id
  );

  // check if the name of the simple event has changed, to determine if we need to update the usages of this name
  // in items that reference this simple event.
  const isDerivedEventNameChanged =
    payloadDerivedEventIndex > -1 &&
    currentDerivedEvents[payloadDerivedEventIndex].name !== nextDerivedEvent.name;

  const nextSubjectVariables = isDerivedEventNameChanged
    ? getNextSubjectVariablesForDerivedEventNameChange(
        currentSubjectVariables,
        currentDerivedEvents[payloadDerivedEventIndex].name,
        nextDerivedEvent.name,
        nextDerivedEvent.type
      )
    : currentSubjectVariables;

  let nextIndexDate: IndexDateVariableLegacy | undefined = currentIndexDate;
  if (
    isDerivedEventNameChanged &&
    currentIndexDate &&
    currentIndexDate.type === IndexDateTypeLegacy.EVENT
  ) {
    const currentIndexDateEventName = currentIndexDate.indexDateValue.split('::')[1];
    if (
      currentIndexDateEventName.slice(1, currentIndexDateEventName.length - 1) ===
      currentDerivedEvents[payloadDerivedEventIndex].name
    ) {
      nextIndexDate = {
        ...currentIndexDate,
        indexDateValue: `INSTANCE_EVENT::"${nextDerivedEvent.name}"`,
      };
    }
  }

  const nameChangedDerivedEvents = isDerivedEventNameChanged
    ? getNextDerivedEventsForDerivedEventNameChange(
        currentDerivedEvents,
        currentDerivedEvents[payloadDerivedEventIndex].name,
        nextDerivedEvent.name,
        nextDerivedEvent.type
      )
    : currentDerivedEvents;

  // If the derived event is already defined in state, replace it with the new one
  // Otherwise, add the new derived event to the end of the array
  const nextDerivedEvents =
    payloadDerivedEventIndex > -1
      ? nameChangedDerivedEvents.map((derivedEvent) => {
          if (derivedEvent.id === nextDerivedEvent.id) {
            let _changeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
            if (computeDiffForCohortBuilderItem(derivedEvent, nextDerivedEvent)) {
              // if derived event previously existed and is different from current update
              if (derivedEvent.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
                // if derived event was new, then it's still new
                _changeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
              } else {
                // derived event is edited
                _changeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
              }
            } else {
              // if derived event previously existed and is the same as current update, then it's unchanged
              _changeStatus =
                derivedEvent.changeStatus ?? CohortBuilderItemChangeStatusLegacy.UNCHANGED;
            }

            return {
              ...nextDerivedEvent,
              changeStatus: _changeStatus,
            };
          }
          return derivedEvent;
        })
      : [
          ...nameChangedDerivedEvents,
          {
            ...nextDerivedEvent,
            changeStatus: CohortBuilderItemChangeStatusLegacy.NEW,
          },
        ];

  const newCohortDsl: CohortDslLegacy = {
    inclusionCriteria: currentCohortDefinition?.cohortDsl.inclusionCriteria ?? [],
    exclusionCriteria: currentCohortDefinition?.cohortDsl.exclusionCriteria ?? [],
    studyPeriodRestrictions: currentCohortDefinition?.cohortDsl.studyPeriodRestrictions ?? [],
    indexDate: nextIndexDate ? transformIndexDateToDsl(nextIndexDate) : undefined,
    subjectVariables: transformSubjectVariablesToDsl(nextSubjectVariables),
    simpleEvents: currentCohortDefinition?.cohortDsl.simpleEvents ?? [],
    derivedEvents: transformDerivedEventsToDsl(nextDerivedEvents),
  };
  return getFullDslQueryString(newCohortDsl);
};

export const getFullCohortDslForDeleteDerivedEvent = (
  currentCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  simpleEventId: string
) => {
  const currentDerivedEvents = currentCohortDefinition?.derivedEvents ?? [];
  const currentCohortDsl = currentCohortDefinition?.cohortDsl;
  const currentDerivedEventsDsl = currentCohortDsl?.derivedEvents ?? [];
  const payloadDerivedEventIndex = currentDerivedEvents.findIndex(
    (derivedEvent) => derivedEvent.id === simpleEventId
  );

  const newCohortDsl: CohortDslLegacy = {
    inclusionCriteria: currentCohortDsl?.inclusionCriteria ?? [],
    exclusionCriteria: currentCohortDsl?.exclusionCriteria ?? [],
    studyPeriodRestrictions: currentCohortDsl?.studyPeriodRestrictions ?? [],
    indexDate: currentCohortDsl?.indexDate,
    subjectVariables: currentCohortDsl?.subjectVariables ?? [],
    simpleEvents: currentCohortDsl?.simpleEvents ?? [],
    derivedEvents: [
      ...currentDerivedEventsDsl.slice(0, payloadDerivedEventIndex),
      ...currentDerivedEventsDsl.slice(payloadDerivedEventIndex + 1),
    ],
  };
  return { newCohortDsl, fullDslQueryString: getFullDslQueryString(newCohortDsl) };
};
